import { motion } from "framer-motion";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, DollarSign, TrendingUp, Clock, BarChart3, Percent, Bitcoin, Wallet, Briefcase, ShieldCheck, Info, Lock, Unlock, PieChart } from "lucide-react";
import TickerSearch from "@/components/TickerSearch";
import { useLanguage } from "@/i18n/LanguageContext";
import { getCurrencySymbol } from "@/lib/currencies";

interface InvestmentFormProps {
  plan: string;
  price: number;
  onSubmit: (data: InvestmentData) => void;
  onBack: () => void;
  runsRemaining?: number | null;
  currency?: string;
}

export interface InvestmentData {
  amount: number;
  riskAllocation: { low: number; medium: number; high: number };
  investmentHorizon: string;
  sectors: string[];
  goal: string;
  plan: string;
  price: number;
  excludedStocks: string[];
  includeCrypto: boolean;
  includeETFs: boolean;
  netWorth?: number;
  annualSalary?: number;
  monthlyExpenses?: number;
  language?: string;
}

const horizonKeys = [
  { value: "3 months", key: "form.h.3months" },
  { value: "6 months", key: "form.h.6months" },
  { value: "1 year", key: "form.h.1year" },
  { value: "2 years", key: "form.h.2years" },
  { value: "5 years", key: "form.h.5years" },
  { value: "10+ years", key: "form.h.10years" },
];

const sectorKeys = [
  { value: "Technology", key: "form.s.technology" },
  { value: "Healthcare", key: "form.s.healthcare" },
  { value: "Finance", key: "form.s.finance" },
  { value: "Energy", key: "form.s.energy" },
  { value: "Consumer Goods", key: "form.s.consumerGoods" },
  { value: "Real Estate", key: "form.s.realEstate" },
  { value: "Industrials", key: "form.s.industrials" },
  { value: "Utilities", key: "form.s.utilities" },
  { value: "AI/ML", key: "form.s.aiml" },
];

const goalKeys = [
  { value: "Wealth Building", key: "form.g.wealthBuilding" },
  { value: "Retirement", key: "form.g.retirement" },
  { value: "Passive Income", key: "form.g.passiveIncome" },
  { value: "Short-term Gains", key: "form.g.shortTermGains" },
  { value: "Diversification", key: "form.g.diversification" },
];

const InvestmentForm = ({ plan, price, onSubmit, onBack, runsRemaining, currency = "USD" }: InvestmentFormProps) => {
  const { t, language } = useLanguage();
  const currSymbol = getCurrencySymbol(currency);
  const [amount, setAmount] = useState("");
  const [lowRisk, setLowRisk] = useState(40);
  const [medRisk, setMedRisk] = useState(35);
  const [highRisk, setHighRisk] = useState(25);
  const [lockedSliders, setLockedSliders] = useState<Record<string, boolean>>({ low: false, med: false, high: false });
  const [horizon, setHorizon] = useState("");
  const [sectors, setSectors] = useState<string[]>([]);
  const [goal, setGoal] = useState("");
  const [excludedStocks, setExcludedStocks] = useState<string[]>([]);
  const [includeCrypto, setIncludeCrypto] = useState(false);
  const [includeETFs, setIncludeETFs] = useState(false);
  const [netWorth, setNetWorth] = useState("");
  const [annualSalary, setAnnualSalary] = useState("");
  const [monthlyExpenses, setMonthlyExpenses] = useState("");

  const isPro = plan === "Pro";
  const isAdvanced = plan !== "Basic";

  const toggleLock = (key: string) => {
    setLockedSliders((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const setRiskValue = (which: "low" | "med" | "high", val: number) => {
    const clamped = Math.max(0, Math.min(100, val));
    if (lockedSliders[which]) return;

    if (which === "low") setLowRisk(clamped);
    else if (which === "med") setMedRisk(clamped);
    else setHighRisk(clamped);
  };

  const riskTotal = lowRisk + medRisk + highRisk;
  const riskValid = riskTotal === 100;

  const toggleSector = (s: string) => setSectors((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  const addExclusion = (ticker: string) => { if (!excludedStocks.includes(ticker)) setExcludedStocks((prev) => [...prev, ticker]); };
  const removeExclusion = (ticker: string) => setExcludedStocks((prev) => prev.filter((t) => t !== ticker));
  const canSubmit = amount && horizon && goal && riskValid;

  const RequiredBadge = () => <span className="text-[10px] text-red-400 font-normal ms-1">{t("form.required")}</span>;
  const OptionalBadge = () => <span className="text-[10px] text-muted-foreground font-normal ms-1">{t("form.optional")}</span>;

  return (
    <section className="py-6 sm:py-12 px-4 sm:px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button onClick={onBack} className="flex items-center gap-2 text-muted-foreground text-sm mb-6 hover:text-foreground transition-colors font-display">
            <ArrowLeft className="w-4 h-4" />
            {t("form.back")}
          </button>

          <div className="mb-6 sm:mb-8 rounded-xl border border-green-500/30 bg-green-500/5 p-3 sm:p-4 flex items-start sm:items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center shrink-0 mt-0.5 sm:mt-0">
              <ShieldCheck className="w-4 h-4 text-green-400" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-display font-semibold text-green-400">{t("form.paymentConfirmed")}</p>
              <p className="text-xs text-muted-foreground break-words">
                {plan} {t("form.planPaid")} | <span className="text-foreground font-medium">{currSymbol}{price.toFixed(2)}</span>
                {runsRemaining != null && (
                  <span className="ms-1 text-gold">{runsRemaining} {t("form.runsRemaining")}</span>
                )}
              </p>
            </div>
          </div>

          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">{t("form.title")}</h2>
          <p className="text-muted-foreground text-sm sm:text-base mb-3 sm:mb-4">{t("form.subtitle")}</p>
          <p className="text-[11px] sm:text-xs text-muted-foreground mb-8 sm:mb-10 flex items-start sm:items-center gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 sm:mt-0" />
            <span>{t("form.requiredNote")} <span className="text-red-400 font-medium">{t("form.requiredWord")}</span> {t("form.mustBeFilled")} <span className="text-muted-foreground font-medium">{t("form.optionalWord")}</span> {t("form.improvesAccuracy")}</span>
          </p>

          <div className="space-y-6 sm:space-y-8">
            {/* Amount */}
            <div>
              <label className="flex items-center gap-2 text-sm font-display font-medium mb-1">
                <DollarSign className="w-4 h-4 text-gold" />
                {t("form.amount")} ({currency})
                <RequiredBadge />
              </label>
              <p className="text-xs text-muted-foreground mb-3">{t("form.amountDesc")}</p>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-display text-lg">{currSymbol}</span>
                <input
                  type="number" min="100" placeholder="e.g. 10000" value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full h-12 rounded-xl bg-secondary border border-border pl-10 pr-4 text-foreground font-display text-lg focus:outline-none focus:ring-2 focus:ring-gold/30 transition-all"
                />
              </div>
            </div>

            {/* Risk Allocation */}
            <div>
              <label className="flex items-center gap-2 text-sm font-display font-medium mb-1">
                <Percent className="w-4 h-4 text-gold" />
                {t("form.riskAllocation")}
                <RequiredBadge />
              </label>
              <p className="text-xs text-muted-foreground mb-2">{t("form.riskAllocDesc")}</p>
              {!riskValid && (
                <div className={`text-xs font-display mb-3 px-3 py-1.5 rounded-lg border ${
                  riskTotal > 100 ? "border-red-400/30 bg-red-400/5 text-red-400" : "border-gold/30 bg-gold/5 text-gold"
                }`}>
                  Total: {riskTotal}% | {riskTotal > 100 ? `reduce by ${riskTotal - 100}%` : `add ${100 - riskTotal}% more`}
                </div>
              )}
              {riskValid && (
                <div className="text-xs font-display mb-3 px-3 py-1.5 rounded-lg border border-green-400/30 bg-green-400/5 text-green-400">
                  ✓ Total: 100%
                </div>
              )}
              <div className="space-y-5">
                {[
                  { label: t("form.lowRisk"), value: lowRisk, key: "low" as const, color: "green-400", desc: t("form.lowRiskDesc") },
                  { label: t("form.medRisk"), value: medRisk, key: "med" as const, color: "gold", desc: t("form.medRiskDesc") },
                  { label: t("form.highRisk"), value: highRisk, key: "high" as const, color: "red-400", desc: t("form.highRiskDesc") },
                ].map(({ label, value, key, color, desc }) => (
                  <div key={key} className={`rounded-xl border bg-card p-3 sm:p-4 ${lockedSliders[key] ? "border-primary/40" : "border-border"}`}>
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        <span className={`w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-${color} shrink-0`} />
                        <span className={`text-xs sm:text-sm font-display font-medium text-${color} truncate`}>{label}</span>
                        <button
                          type="button"
                          onClick={() => toggleLock(key)}
                          className={`p-1 rounded-md transition-colors shrink-0 ${lockedSliders[key] ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground"}`}
                          title={lockedSliders[key] ? "Unlock" : "Lock value"}
                        >
                          {lockedSliders[key] ? <Lock className="w-3 sm:w-3.5 h-3 sm:h-3.5" /> : <Unlock className="w-3 sm:w-3.5 h-3 sm:h-3.5" />}
                        </button>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={value}
                          disabled={lockedSliders[key]}
                          onChange={(e) => setRiskValue(key, parseInt(e.target.value) || 0)}
                          className={`w-14 sm:w-16 h-8 rounded-lg bg-secondary border border-border px-1.5 sm:px-2 text-center text-sm font-display font-bold text-${color} focus:outline-none focus:ring-2 focus:ring-ring ${lockedSliders[key] ? "opacity-50 cursor-not-allowed" : ""}`}
                        />
                        <span className={`text-xs sm:text-sm font-display text-${color}`}>%</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground mb-3">{desc}</p>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={value}
                      disabled={lockedSliders[key]}
                      onChange={(e) => setRiskValue(key, parseInt(e.target.value) || 0)}
                      className={`w-full accent-primary ${lockedSliders[key] ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Horizon */}
            <div>
              <label className="flex items-center gap-2 text-sm font-display font-medium mb-1">
                <Clock className="w-4 h-4 text-gold" />
                {t("form.horizon")}
                <RequiredBadge />
              </label>
              <p className="text-xs text-muted-foreground mb-3">{t("form.horizonDesc")}</p>
              <div className="flex flex-wrap gap-2">
                {horizonKeys.map(({ value, key }) => (
                  <button key={value} onClick={() => setHorizon(value)}
                    className={`px-4 py-2 rounded-lg border text-sm font-display transition-all ${
                      horizon === value ? "border-gold/50 bg-gold/10 text-gold" : "border-border bg-card text-muted-foreground hover:border-gold/20"
                    }`}
                  >{t(key)}</button>
                ))}
              </div>
            </div>

            {/* Sectors */}
            <div>
              <label className="flex items-center gap-2 text-sm font-display font-medium mb-1">
                <TrendingUp className="w-4 h-4 text-gold" />
                {t("form.sectors")}
                <OptionalBadge />
              </label>
              <p className="text-xs text-muted-foreground mb-3">{t("form.sectorsDesc")}</p>
              <div className="flex flex-wrap gap-2">
                {sectorKeys.map(({ value, key }) => (
                  <button key={value} onClick={() => toggleSector(value)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-display transition-all ${
                      sectors.includes(value) ? "border-gold/50 bg-gold/10 text-gold" : "border-border bg-card text-muted-foreground hover:border-gold/20"
                    }`}
                  >{t(key)}</button>
                ))}
              </div>
            </div>

            {/* Crypto */}
            {isAdvanced && (
              <div>
                <label className="flex items-center gap-2 text-sm font-display font-medium mb-1">
                  <Bitcoin className="w-4 h-4 text-gold" />
                  {t("form.crypto")}
                  <OptionalBadge />
                </label>
                <p className="text-xs text-muted-foreground mb-3">{t("form.cryptoDesc")}</p>
                <button onClick={() => setIncludeCrypto(!includeCrypto)}
                  className={`px-4 py-2.5 rounded-lg border text-sm font-display transition-all flex items-center gap-2 ${
                    includeCrypto ? "border-gold/50 bg-gold/10 text-gold" : "border-border bg-card text-muted-foreground hover:border-gold/20"
                  }`}
                >
                  <Bitcoin className="w-4 h-4" />
                  {includeCrypto ? t("form.cryptoIncluded") : t("form.cryptoAdd")}
                </button>
              </div>
            )}

            {/* ETFs */}
            <div>
              <label className="flex items-center gap-2 text-sm font-display font-medium mb-1">
                <PieChart className="w-4 h-4 text-gold" />
                {t("form.etf")}
                <OptionalBadge />
              </label>
              <p className="text-xs text-muted-foreground mb-3">{t("form.etfDesc")}</p>
              <button onClick={() => setIncludeETFs(!includeETFs)}
                className={`px-4 py-2.5 rounded-lg border text-sm font-display transition-all flex items-center gap-2 ${
                  includeETFs ? "border-gold/50 bg-gold/10 text-gold" : "border-border bg-card text-muted-foreground hover:border-gold/20"
                }`}
              >
                <PieChart className="w-4 h-4" />
                {includeETFs ? t("form.etfIncluded") : t("form.etfAdd")}
              </button>
            </div>

            <TickerSearch excludedStocks={excludedStocks} onAdd={addExclusion} onRemove={removeExclusion} />

            {/* Goal */}
            <div>
              <label className="flex items-center gap-2 text-sm font-display font-medium mb-1">
                <BarChart3 className="w-4 h-4 text-gold" />
                {t("form.goal")}
                <RequiredBadge />
              </label>
              <p className="text-xs text-muted-foreground mb-3">{t("form.goalDesc")}</p>
              <div className="flex flex-wrap gap-2">
                {goalKeys.map(({ value, key }) => (
                  <button key={value} onClick={() => setGoal(value)}
                    className={`px-4 py-2 rounded-lg border text-sm font-display transition-all ${
                      goal === value ? "border-gold/50 bg-gold/10 text-gold" : "border-border bg-card text-muted-foreground hover:border-gold/20"
                    }`}
                  >{t(key)}</button>
                ))}
              </div>
            </div>

            {/* Financial Profile */}
            {isPro && (
              <div className="rounded-2xl border border-gold/20 bg-gradient-card p-4 sm:p-6">
                <h3 className="font-display text-lg font-semibold mb-1 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-gold" />
                  {t("form.financialProfile")}
                  <OptionalBadge />
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold/10 border border-gold/20 text-gold">PRO</span>
                </h3>
                <p className="text-xs text-muted-foreground mb-2">{t("form.financialProfileDesc")}</p>
                <div className="flex items-center gap-1.5 mb-5">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
                  <p className="text-[11px] text-green-400/80">{t("form.privacyNote")}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center gap-2 text-xs font-display font-medium mb-1">
                      <Wallet className="w-3.5 h-3.5 text-gold" />
                      {t("form.netWorth")} ({currency})
                    </label>
                    <p className="text-[10px] text-muted-foreground mb-2">{t("form.netWorthDesc")}</p>
                    <input type="number" placeholder={`e.g. ${currSymbol}250000`} value={netWorth} onChange={(e) => setNetWorth(e.target.value)}
                      className="w-full h-10 rounded-lg bg-secondary border border-border px-3 text-foreground font-display text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 transition-all" />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-xs font-display font-medium mb-1">
                      <DollarSign className="w-3.5 h-3.5 text-gold" />
                      {t("form.salary")} ({currency})
                    </label>
                    <p className="text-[10px] text-muted-foreground mb-2">{t("form.salaryDesc")}</p>
                    <input type="number" placeholder={`e.g. ${currSymbol}85000`} value={annualSalary} onChange={(e) => setAnnualSalary(e.target.value)}
                      className="w-full h-10 rounded-lg bg-secondary border border-border px-3 text-foreground font-display text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 transition-all" />
                  </div>
                  <div>
                    <label className="text-xs font-display font-medium mb-1 block">{t("form.expenses")} ({currency})</label>
                    <p className="text-[10px] text-muted-foreground mb-2">{t("form.expensesDesc")}</p>
                    <input type="number" placeholder={`e.g. ${currSymbol}3500`} value={monthlyExpenses} onChange={(e) => setMonthlyExpenses(e.target.value)}
                      className="w-full h-10 rounded-lg bg-secondary border border-border px-3 text-foreground font-display text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 transition-all" />
                  </div>
                </div>
              </div>
            )}

            <Button variant="gold" size="xl" className="w-full" disabled={!canSubmit}
            onClick={() => onSubmit({
                amount: parseFloat(amount),
                riskAllocation: { low: lowRisk, medium: medRisk, high: highRisk },
                investmentHorizon: horizon, sectors, goal, plan, price, excludedStocks, includeCrypto, includeETFs,
                language,
                ...(isPro && netWorth ? { netWorth: parseFloat(netWorth) } : {}),
                ...(isPro && annualSalary ? { annualSalary: parseFloat(annualSalary) } : {}),
                ...(isPro && monthlyExpenses ? { monthlyExpenses: parseFloat(monthlyExpenses) } : {}),
              })}
            >
              {t("form.submit")}
              <ArrowRight className="w-5 h-5" />
            </Button>

            <p className="text-center text-xs text-muted-foreground mt-2">{t("results.disclaimer")}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InvestmentForm;
